import {
  formatBrowserCode,
  getDefaultPttBrowserCode,
  normalizePttCode,
  PTT_BLOCKED_KEYS,
  PTT_BLOCKED_MOUSE_BUTTONS,
} from "@bezoned/shared/features/push-to-talk/push-to-talk.utils.js";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePushToTalkStore } from "./push-to-talk.store.js";

interface CurrentKeyInfo {
  code: string;
  display: string;
  isCustom: boolean;
}

interface TempKey {
  code: string;
  display: string;
}

const EMPTY_TEMP_KEY: TempKey = { code: "", display: "" };
const DEFAULT_PTT_BROWSER_CODE = getDefaultPttBrowserCode();
const BLOCKED_KEY_ERROR_TIMEOUT_MS = 3000;

export const useSetPushToTalkButton = () => {
  const { code, setCode, resetToDefault } = usePushToTalkStore();

  const [isRecording, setIsRecording] = useState(false);
  const [tempKey, setTempKey] = useState<TempKey>(EMPTY_TEMP_KEY);
  const [validationError, setValidationError] = useState("");
  const blockedKeyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearValidationErrorTimer = useCallback(() => {
    if (blockedKeyTimerRef.current) {
      clearTimeout(blockedKeyTimerRef.current);
      blockedKeyTimerRef.current = null;
    }
  }, []);

  const clearValidationError = useCallback(() => {
    clearValidationErrorTimer();
    setValidationError("");
  }, [clearValidationErrorTimer]);

  const getCurrentKeyInfo = useCallback((): CurrentKeyInfo => {
    const display = formatBrowserCode(code);
    const isCustom = code !== DEFAULT_PTT_BROWSER_CODE;

    if (!isCustom) {
      return {
        code,
        display: `Default (${display})`,
        isCustom: false,
      };
    }

    return { code, display, isCustom: true };
  }, [code]);

  const startRecording = useCallback(() => {
    setIsRecording(true);
    setTempKey(EMPTY_TEMP_KEY);
    clearValidationError();
  }, [clearValidationError]);

  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    setTempKey(EMPTY_TEMP_KEY);
    clearValidationError();
  }, [clearValidationError]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isRecording) {
        return;
      }

      event.preventDefault();

      // Escape cancels recording and returns to previous value
      if (event.code === "Escape") {
        cancelRecording();
        return;
      }

      const normalizedCode = normalizePttCode(event.code);

      if (PTT_BLOCKED_KEYS.has(event.code)) {
        setValidationError("This key cannot be used for push-to-talk.");
        clearValidationErrorTimer();
        blockedKeyTimerRef.current = setTimeout(() => {
          setValidationError("");
        }, BLOCKED_KEY_ERROR_TIMEOUT_MS);
        return; // stay in recording mode
      }

      // Valid key — clear any pending error and timer
      clearValidationError();

      setTempKey({
        code: normalizedCode,
        display: formatBrowserCode(normalizedCode),
      });

      setIsRecording(false);
    },
    [
      isRecording,
      cancelRecording,
      clearValidationError,
      clearValidationErrorTimer,
    ]
  );

  // Mouse button capture during recording.
  // Blocked buttons (left, right) show an error without preventDefault,
  // so UI buttons like "Cancel" remain clickable.
  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (PTT_BLOCKED_MOUSE_BUTTONS.has(event.button)) {
        setValidationError(
          "This mouse button cannot be used for push-to-talk."
        );
        clearValidationErrorTimer();
        blockedKeyTimerRef.current = setTimeout(() => {
          setValidationError("");
        }, BLOCKED_KEY_ERROR_TIMEOUT_MS);
        return; // no preventDefault — let UI clicks through
      }

      event.preventDefault();
      event.stopPropagation();

      const syntheticCode = `Mouse${event.button}`;

      clearValidationError();

      setTempKey({
        code: syntheticCode,
        display: formatBrowserCode(syntheticCode),
      });

      setIsRecording(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isRecording, clearValidationError, clearValidationErrorTimer]);

  // Auto-save when recording ends with a valid temp key
  useEffect(() => {
    if (!isRecording && tempKey.code && !validationError) {
      setCode(tempKey.code);
      setTempKey(EMPTY_TEMP_KEY);
    }
  }, [isRecording, tempKey.code, validationError, setCode]);

  // Clear any pending validation timer on unmount
  useEffect(
    () => () => clearValidationErrorTimer(),
    [clearValidationErrorTimer]
  );

  return {
    cancelRecording,
    currentKey: getCurrentKeyInfo(),
    handleKeyDown,
    isRecording,
    resetKey: resetToDefault,
    startRecording,
    tempKey,
    validationError,
  };
};
